/**
 * One violin, bowed by proximity.
 *
 * Two sawtooths a few cents apart give the string its beating; a band of pink
 * noise gives the bow its grain; three peaking filters stand in for the body
 * — the air resonance near 280 Hz, the main wood near 460, and the bridge hill
 * up at 2.5 kHz that makes a violin sound like a violin and not an organ.
 *
 * `bow` ∈ [0,1] is set from how near the traveller is; it moves the volume,
 * the brightness and the amount of noise together, the way a real bow does.
 */
export class ViolinVoice {
  /** @param {import('../AudioEngine.js').AudioEngine} engine */
  constructor(engine, hz) {
    this.engine = engine;
    this.ac = engine.ac;
    this.hz = hz;
    this.bow = 0;
    this.started = false;
  }

  start() {
    if (this.started) return;
    const ac = this.ac;
    const now = ac.currentTime;

    this.out = ac.createGain();
    this.out.gain.value = 0.0001;

    this.pan = ac.createStereoPanner();
    this.pan.pan.value = 0;
    this.out.connect(this.pan);
    this.pan.connect(this.engine.dry);
    this.pan.connect(this.engine.send);

    // Body: three resonances in series, then the rolloff of varnished wood.
    const body = [
      { f: 280, q: 2.2, g: 7 },
      { f: 462, q: 2.6, g: 6 },
      { f: 2500, q: 1.1, g: 5 },
    ].map(({ f, q, g }) => {
      const b = ac.createBiquadFilter();
      b.type = 'peaking';
      b.frequency.value = f;
      b.Q.value = q;
      b.gain.value = g;
      return b;
    });

    this.tone = ac.createBiquadFilter();
    this.tone.type = 'lowpass';
    this.tone.frequency.value = 900;
    this.tone.Q.value = 0.9;

    let node = this.tone;
    for (const b of body) {
      node.connect(b);
      node = b;
    }
    node.connect(this.out);
    this.body = body;

    // Strings.
    this.oscs = [];
    const makeOsc = (type, detune, gain) => {
      const o = ac.createOscillator();
      o.type = type;
      o.frequency.value = this.hz;
      o.detune.value = detune;
      const g = ac.createGain();
      g.gain.value = gain;
      o.connect(g);
      g.connect(this.tone);
      o.start(now);
      this.oscs.push(o);
      return o;
    };
    makeOsc('sawtooth', -4.5, 0.34);
    makeOsc('sawtooth', 5.5, 0.3);
    makeOsc('triangle', 0, 0.18);
    // A sympathetic octave: the other strings ringing.
    const oct = makeOsc('sine', 2, 0.1);
    oct.frequency.value = this.hz * 2;

    // Vibrato, slow and narrow — a held note, not a performance.
    this.vib = ac.createOscillator();
    this.vib.type = 'sine';
    this.vib.frequency.value = 4.6 + Math.random() * 0.9;
    this.vibGain = ac.createGain();
    this.vibGain.gain.value = 5.5;
    this.vib.connect(this.vibGain);
    for (const o of this.oscs) this.vibGain.connect(o.detune);
    this.vib.start(now);

    // Bow noise.
    this.noise = ac.createBufferSource();
    this.noise.buffer = this.engine.noiseBuffer;
    this.noise.loop = true;
    this.noiseBand = ac.createBiquadFilter();
    this.noiseBand.type = 'bandpass';
    this.noiseBand.frequency.value = this.hz * 5;
    this.noiseBand.Q.value = 0.8;
    this.noiseGain = ac.createGain();
    this.noiseGain.gain.value = 0.0001;
    this.noise.connect(this.noiseBand);
    this.noiseBand.connect(this.noiseGain);
    this.noiseGain.connect(this.out);
    this.noise.start(now);

    this.started = true;
  }

  /**
   * @param {number} bow 0 silent, 1 full pressure
   * @param {number} pan -1..1
   * @param {number} [shimmer] extra brightness while being looked at
   */
  set(bow, pan = 0, shimmer = 0) {
    if (!this.started) return;
    const now = this.ac.currentTime;
    const b = Math.max(0, Math.min(1, bow));
    this.bow = b;

    const level = Math.pow(b, 1.6) * 0.3;
    this.out.gain.setTargetAtTime(Math.max(level, 0.00001), now, 0.18);
    this.tone.frequency.setTargetAtTime(520 + b * 2200 + shimmer * 2600, now, 0.22);
    this.noiseGain.gain.setTargetAtTime(Math.max(b * 0.03 + shimmer * 0.02, 0.00001), now, 0.2);
    this.vibGain.gain.setTargetAtTime(3.5 + b * 4 + shimmer * 6, now, 0.4);
    this.pan.pan.setTargetAtTime(Math.max(-1, Math.min(1, pan)), now, 0.25);
  }

  /** One swell, when a violin is remembered. */
  swell() {
    if (!this.started) return;
    const now = this.ac.currentTime;
    this.out.gain.cancelScheduledValues(now);
    this.out.gain.setValueAtTime(Math.max(this.out.gain.value, 0.0001), now);
    this.out.gain.exponentialRampToValueAtTime(0.4, now + 0.5);
    this.out.gain.exponentialRampToValueAtTime(0.00001, now + 5.5);
    this.tone.frequency.cancelScheduledValues(now);
    this.tone.frequency.setTargetAtTime(4200, now, 0.3);
    this.tone.frequency.setTargetAtTime(600, now + 1.2, 1.6);
  }

  stop() {
    if (!this.started) return;
    const now = this.ac.currentTime;
    this.out.gain.setTargetAtTime(0.00001, now, 0.3);
    for (const o of this.oscs) o.stop(now + 1.5);
    this.vib.stop(now + 1.5);
    this.noise.stop(now + 1.5);
    this.started = false;
  }
}
